import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep, get, set } from "lodash";
import { v4 as uuidv4 } from "uuid";

export interface FormSubmitResult {
  success: boolean;
  itemLink: string;
  error?: string;
}

function getDefaultMeta() {
  return {
    submitted: false,
    submitting: false,
    validating: false,
    asyncValidating: false,
    valid: false,
    onBlurCounter: 0,
    onChangeCounter: 0,
    validationCallId: uuidv4(),
    previewing: false,
    debugging: false,
    applied: false,
  };
}

export interface FormInstanceMeta {
  submitted: boolean;
  submitting: boolean;
  validating: boolean;
  asyncValidating: boolean;
  valid: boolean;
  onBlurCounter: number;
  onChangeCounter: number;
  validationCallId: string;
  previewing: boolean;
  debugging: boolean;
  applied: boolean;
}

export interface FormInstance {
  initialValues: any;
  values: any;
  errors: any;
  dirty: any;
  warnings: any;
  fieldStates: any; // modified focused visited touched simulateTouched dirty valid error warning
  meta: FormInstanceMeta;
}

export const formSlice = createSlice({
  name: "form",
  initialState: {
    instances: {},
    domain: undefined,
  },
  reducers: {
    setDomain(state, action: PayloadAction<any>) {
      state.domain = action.payload;
    },
    set: (state, action: PayloadAction<{ id: string; path: string; value: any }>) => {
      set(state, `instances.${action.payload.id}.${action.payload.path}`, cloneDeep(action.payload.value));
    },
    setValue: (state, action: PayloadAction<{ id: string; path: string; value: any }>) => {
      set(state, `instances.${action.payload.id}.values.${action.payload.path}`, cloneDeep(action.payload.value));
      if (state.instances[action.payload.id]?.meta?.applied) {
        state.instances[action.payload.id].meta.applied = false;
      }
    },
    setError: (state, action: PayloadAction<{ id: string; path: string; value: any }>) => {
      set(state, `instances.${action.payload.id}.errors.${action.payload.path}`, cloneDeep(action.payload.value));
    },
    setWarning: (state, action: PayloadAction<{ id: string; path: string; value: any }>) => {
      set(state, `instances.${action.payload.id}.warnings.${action.payload.path}`, cloneDeep(action.payload.value));
    },
    setFieldState: (state, action: PayloadAction<{ id: string; path: string; value: any }>) => {
      set(state, `instances.${action.payload.id}.fieldStates.${action.payload.path}`, cloneDeep(action.payload.value));
    },
    triggerValidation: (state, action: PayloadAction<string>) => {
      const onBlurCounter = get(state, `instances.${action.payload}.meta.onBlurCounter`);
      const newValue = Number(onBlurCounter) + 1;
      set(state, `instances.${action.payload}.meta.onBlurCounter`, newValue);
    },
    setInitialValues: (state, action: PayloadAction<{ id: string; values: any }>) => {
      const id = cloneDeep(action.payload.id);
      const initialValues = cloneDeep(action.payload.values);
      state.instances[id] = {
        initialValues: initialValues,
        values: initialValues,
        errors: {},
        warnings: {},
        dirty: {},
        fieldStates: {},
        meta: getDefaultMeta(),
      };
    },
    setValues: (state, action: PayloadAction<{ id: string; values: any }>) => {
      const id = action.payload.id;
      const values = cloneDeep(action.payload.values);
      state.instances[id].values = values;
    },
    setErrors: (state, action: PayloadAction<{ id: string; errors: any }>) => {
      const id = action.payload.id;
      const errors = cloneDeep(action.payload.errors);
      state.instances[id].errors = errors;
    },
    setWarnings: (state, action: PayloadAction<{ id: string; warnings: any }>) => {
      const id = action.payload.id;
      const warnings = cloneDeep(action.payload.warnings);
      state.instances[id].warnings = warnings;
    },
    setDirty: (state, action: PayloadAction<{ id: string; dirty: string[] }>) => {
      const id = action.payload.id;
      const dirty = cloneDeep(action.payload.dirty);
      state.instances[id].dirty = dirty;
    },
    setFieldStates: (state, action: PayloadAction<{ id: string; fieldStates: any }>) => {
      const id = action.payload.id;
      const fieldStates = cloneDeep(action.payload.fieldStates);
      state.instances[id].fieldStates = fieldStates;
    },
    reset: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const initialValues = state.instances[id].initialValues;
      state.instances[id] = {
        initialValues: initialValues,
        values: initialValues,
        errors: {},
        warnings: {},
        dirty: {},
        fieldStates: {},
        meta: getDefaultMeta(),
      };
    },
    confirmSave: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const instance = state.instances[id];
      if (instance) {
        instance.initialValues = cloneDeep(instance.values);
        instance.dirty = {};
      }
    },
    clear: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.instances[id] = {
        initialValues: {},
        values: {},
        errors: {},
        warnings: {},
        dirty: {},
        fieldStates: {},
        meta: getDefaultMeta(),
      };
    },
  },
});

export const formActions = formSlice.actions;

export const formReducer = formSlice.reducer;
